/**
 * SessionDetail
 * Answers and photos for one photo booth session.
 */

import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { PhotoSession } from '@/services/guest.service';
import type { Questionnaire } from '@/types';
import {
  formatLongDateTime,
  formatResponseKey,
  formatResponseValue,
  isPhotoStripUrl,
  toDate,
} from '@/utils/guestFormat';

type SessionDetailProps = {
  session: PhotoSession;
  questionnaire?: Questionnaire;
  onOpenPhoto: (url: string) => void;
};

const EMAIL_STATUS_LABEL: Record<PhotoSession['emailStatus'], string> = {
  sent: 'Emailed',
  pending: 'Email pending',
  failed: 'Email failed',
};

export function EmailStatusChip({
  status,
}: {
  status: PhotoSession['emailStatus'];
}) {
  const label = EMAIL_STATUS_LABEL[status] ?? status;
  return (
    <span className={`email-chip ${status}`}>
      <span className={`status-dot ${status}`} aria-hidden="true" />
      {label}
    </span>
  );
}

function orderResponseKeys(
  responses: PhotoSession['responses'],
  questionnaire?: Questionnaire
): string[] {
  const orderedQuestionIds = (questionnaire?.questions ?? [])
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((q) => q.id);

  const responseKeys = Object.keys(responses);

  return [
    ...orderedQuestionIds.filter((id) => id in responses),
    ...responseKeys.filter((key) => !orderedQuestionIds.includes(key)),
  ];
}

export function SessionDetail({
  session,
  questionnaire,
  onOpenPhoto,
}: SessionDetailProps) {
  const reduceMotion = useReducedMotion();
  const createdAt = toDate(session.createdAt);
  const responses = session.responses ?? {};
  const orderedKeys = orderResponseKeys(responses, questionnaire).filter(
    (key) => responses[key] !== undefined
  );
  const photoUrls = session.photoUrls ?? [];

  return (
    <motion.div
      className="session-detail"
      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
    >
      <div className="session-detail-header">
        <div className="session-detail-heading">
          <div className="session-eyebrow">Session</div>
          <div className="session-title">
            {createdAt ? formatLongDateTime(createdAt) : 'Unknown time'}
          </div>
        </div>
        <EmailStatusChip status={session.emailStatus} />
      </div>

      <div className="session-detail-body">
        <section className="session-section" aria-label="Questionnaire answers">
          <h4 className="session-section-label">
            Answers
            <span className="session-section-count">{orderedKeys.length}</span>
          </h4>
          {orderedKeys.length === 0 ? (
            <p className="session-section-empty">No answers recorded.</p>
          ) : (
            <dl className="answer-list">
              {orderedKeys.map((key) => {
                const label =
                  questionnaire?.questions.find((q) => q.id === key)?.label ??
                  formatResponseKey(key);
                const value = formatResponseValue(responses[key]);
                return (
                  <div key={key} className="answer-item">
                    <dt>{label}</dt>
                    {value.trim() === '' ? (
                      <dd className="is-empty">—</dd>
                    ) : (
                      <dd>{value}</dd>
                    )}
                  </div>
                );
              })}
            </dl>
          )}
        </section>

        <section className="session-section" aria-label="Session photos">
          <h4 className="session-section-label">
            Photos
            <span className="session-section-count">{photoUrls.length}</span>
          </h4>
          {photoUrls.length === 0 ? (
            <p className="session-section-empty">No photos saved.</p>
          ) : (
            <div className="photo-row">
              {photoUrls.map((url, index) => (
                <PhotoTile
                  key={url}
                  url={url}
                  index={index}
                  isStrip={isPhotoStripUrl(url)}
                  onOpen={onOpenPhoto}
                  reduceMotion={Boolean(reduceMotion)}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </motion.div>
  );
}

type PhotoTileProps = {
  url: string;
  index: number;
  isStrip: boolean;
  onOpen: (url: string) => void;
  reduceMotion: boolean;
};

function PhotoTile({
  url,
  index,
  isStrip,
  onOpen,
  reduceMotion,
}: PhotoTileProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [dims, setDims] = useState<{ width: number; height: number } | null>(
    null
  );

  // Cached images can finish loading before React attaches the load handler.
  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      setDims({ width: img.naturalWidth, height: img.naturalHeight });
    }
  }, []);

  const isLoaded = dims !== null;
  const isLandscape = dims ? dims.width > dims.height : false;
  const label = isStrip ? 'Photo strip' : `Photo ${index + 1}`;
  const className = [
    'photo-tile',
    isStrip && 'is-strip',
    isLandscape && 'is-landscape',
    isLoaded && 'is-loaded',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <motion.button
      type="button"
      className={className}
      // The tile takes the photo's real shape once known; until then the
      // stylesheet's portrait placeholder ratio applies.
      style={
        dims ? { aspectRatio: `${dims.width} / ${dims.height}` } : undefined
      }
      onClick={() => onOpen(url)}
      aria-label={`Open ${label.toLowerCase()}`}
      initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: 0.26,
        ease: 'easeOut',
        delay: reduceMotion ? 0 : 0.05 + index * 0.05,
      }}
    >
      <img
        ref={imgRef}
        src={url}
        alt={label}
        loading="lazy"
        onLoad={(e) => {
          const img = e.currentTarget;
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            setDims({ width: img.naturalWidth, height: img.naturalHeight });
          }
        }}
      />
      {isStrip && <span className="photo-tile-tag">Strip</span>}
    </motion.button>
  );
}
